import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SealCheck } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { Badge, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { useReleaseQueue, useSkuOptions, type QueueState, type ReleaseQueueRow } from '../../api/lab'
import { ActionButton } from '../../components/Can'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, Status, UserName, When } from '../../components/bits'
import { PackLotState } from '../../components/lab/PackLotState'
import { ReleaseDecisionDialog } from '../../components/lab/ReleaseDecisionDialog'
import { useUrlState } from '../../lib/useUrlState'
import { DECISION_LABEL, DECISION_TONE, holdReasonText } from '../../model/lab/labText'
import { releasableState } from '../../model/lab/releaseDecisionForm'
import { humanize } from '../../labels'

const LIMIT = 25

const TABS: Array<{ value: QueueState; label: string }> = [
  { value: 'WAITING', label: 'Waiting for release' },
  { value: 'HELD', label: 'On hold' },
  { value: 'RELEASED', label: 'Released' },
  { value: 'ALL', label: 'All' },
]

/** Release queue (F7.6-d): closed pack lots waiting for a named quality person, held ones, released ones. */
export function ReleaseQueuePage() {
  const navigate = useNavigate()
  const [f, setF] = useUrlState({ state: 'WAITING', skuId: '', search: '', page: 1 })
  const state = (TABS.some((t) => t.value === f.state) ? f.state : 'WAITING') as QueueState
  const skus = useSkuOptions()
  const q = useReleaseQueue({ state, page: f.page, limit: LIMIT, skuId: f.skuId || undefined, search: f.search || undefined })
  const [deciding, setDeciding] = useState<ReleaseQueueRow | null>(null)

  const columns: Column<ReleaseQueueRow>[] = [
    { id: 'code', header: 'Pack lot', cell: (r) => <Code className="whitespace-nowrap">{r.code}</Code> },
    {
      id: 'sku',
      header: 'Product',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.skuName ?? '—'}</span>
          {r.skuCode && <span className="font-mono text-[11.5px] text-muted">{r.skuCode}</span>}
        </span>
      ),
    },
    { id: 'packed', header: 'Packed', cell: (r) => <When at={r.packDate} dateOnly />, hideBelow: 'md' },
    { id: 'bb', header: 'Best before', cell: (r) => <When at={r.bestBefore} dateOnly />, hideBelow: 'lg' },
    { id: 'pouches', header: 'Pouches', align: 'right', mono: true, cell: (r) => r.pouchCount ?? '—' },
    { id: 'state', header: 'Status', cell: (r) => <PackLotState status={r.status} hold={r.hold} short /> },
    {
      id: 'latest',
      header: 'Last decision',
      hideBelow: 'md',
      cell: (r) =>
        r.latest ? (
          <span className="flex flex-col gap-0.5 leading-tight">
            <span className="flex flex-wrap items-center gap-1">
              <Status status={DECISION_TONE[r.latest.decision] ?? r.latest.decision} label={DECISION_LABEL[r.latest.decision] ?? humanize(r.latest.decision)} />
              {r.latest.auto && <Badge tone="neutral">Automatic</Badge>}
            </span>
            {r.latest.reason && <span className="max-w-[22rem] truncate text-[12.5px]" title={r.latest.reason}>{holdReasonText(r.latest.reason)}</span>}
            <span className="text-[12px] text-muted">
              {!r.latest.auto && <UserName id={r.latest.decidedById} />} · <When at={r.latest.decidedAt} relative />
            </span>
          </span>
        ) : (
          <span className="text-muted">None yet</span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <ActionButton
            perm="pack-lots:release"
            size="sm"
            variant={releasableState(r.status, r.hold) ? 'primary' : 'ghost'}
            leadingIcon={<SealCheck />}
            disabled={r.status === 'DESTROYED'}
            onClick={() => setDeciding(r)}
          >
            Decide
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Release queue"
      description="Closed pack lots stay at the hall until a quality person who did not run or clean the line signs their release. Open one to read its checklist, then release or hold it."
    >
      <div role="tablist" aria-label="Release state" className="flex flex-wrap gap-1 border-b border-rule">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={state === t.value}
            onClick={() => setF({ state: t.value })}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-[14px]',
              state === t.value ? 'border-ink font-semibold text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <DataTable<ReleaseQueueRow>
        caption="Release queue"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/pack-lots/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={() => setF({ skuId: '', search: '' })} active={Boolean(f.skuId) || Boolean(f.search)}>
            <SearchFilter label="Pack lot code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. PL-2026-1010" />
            <SelectFilter
              label="Product"
              value={f.skuId}
              onChange={(skuId) => setF({ skuId })}
              options={(skus.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<SealCheck />}
            title={state === 'WAITING' ? 'Nothing waiting for release' : state === 'HELD' ? 'Nothing on hold' : 'No pack lots here'}
            description="A pack lot joins the queue when its pack run is closed on the phone."
            compact
          />
        }
      />
      {deciding && (
        <ReleaseDecisionDialog
          packLotId={deciding.id}
          initialDecision={releasableState(deciding.status, deciding.hold) ? 'RELEASE' : 'HOLD'}
          onClose={() => setDeciding(null)}
        />
      )}
    </ConsolePage>
  )
}
